import { ARENA_NOTICE_MS, type ArenaEvent } from '@xox/shared'
import { AnimatePresence, motion } from 'motion/react'
import { type ReactNode, useEffect } from 'react'
import { connectArenaFeed } from '../../lib/arenaFeed'
import { useArena } from '../../store/arena'
import { useSession } from '../../store/session'
import { useToday } from '../../store/today'
import { ChallengeCard } from './ChallengeCard'
import { QuestToast } from './QuestToast'
import { RecordCard } from './RecordCard'

/** Cards on screen at once; a challenge outranks a quest toast, which outranks a record. */
const MAX_VISIBLE = 2

/**
 * Everything the arena feed drops in under the header: record notices, challenge cards and
 * quest toasts. Absolutely positioned inside the sticky header, so it never takes layout space
 * and follows the header on every page. The feed opens once the player is known, so the server
 * can count this page as present.
 */
export function ArenaNotices() {
  const playerId = useSession((s) => s.me?.playerId)
  const notice = useArena((s) => s.notice)
  const challenges = useArena((s) => s.challenges)
  const toasts = useArena((s) => s.toasts)
  const receive = useArena((s) => s.receive)
  const dismiss = useArena((s) => s.dismiss)
  const dismissChallenge = useArena((s) => s.dismissChallenge)
  const dismissToast = useArena((s) => s.dismissToast)

  useEffect(() => (playerId ? connectArenaFeed(receive) : undefined), [playerId, receive])
  // A quest just completed: the Today screen's meters are out of date.
  useEffect(() => {
    if (toasts.length > 0) void useToday.getState().load(true)
  }, [toasts])
  useEffect(() => {
    if (!notice) return
    const id = setTimeout(dismiss, ARENA_NOTICE_MS)
    return () => clearTimeout(id)
  }, [notice, dismiss])

  const cards: { ev: ArenaEvent; body: ReactNode; onDismiss: () => void; className: string }[] = [
    ...challenges.map((ev) => ({
      ev,
      body: <ChallengeCard ev={ev} onDone={() => dismissChallenge(ev.id)} />,
      onDismiss: () => dismissChallenge(ev.id),
      className:
        'bg-white text-slate-900 ring-slate-200 dark:bg-slate-900 dark:text-white dark:ring-slate-700',
    })),
    ...toasts.map((ev) => ({
      ev,
      body: <QuestToast ev={ev} onDismiss={() => dismissToast(ev.id)} />,
      onDismiss: () => dismissToast(ev.id),
      className: 'bg-emerald-600 text-white ring-emerald-400/40',
    })),
    ...(notice
      ? [{ ev: notice, body: <RecordCard ev={notice} />, onDismiss: dismiss, className: '' }]
      : []),
  ].slice(0, MAX_VISIBLE)

  return (
    <div className="pointer-events-none absolute inset-x-0 top-full z-30 flex flex-col items-center gap-2 px-3 pt-2">
      <AnimatePresence>
        {cards.map((card) => (
          <NoticeShell key={card.ev.id} className={card.className} onDismiss={card.onDismiss}>
            {card.body}
          </NoticeShell>
        ))}
      </AnimatePresence>
    </div>
  )
}

/** The card frame every notice shares: springs in, swipes up to dismiss. */
function NoticeShell({
  children,
  className,
  onDismiss,
}: {
  children: ReactNode
  className: string
  onDismiss: () => void
}) {
  return (
    <motion.div
      role="status"
      aria-live="polite"
      initial={{ y: -24, opacity: 0, scale: 0.96 }}
      animate={{ y: 0, opacity: 1, scale: 1 }}
      exit={{ y: -24, opacity: 0 }}
      transition={{ type: 'spring', stiffness: 420, damping: 30 }}
      drag="y"
      dragConstraints={{ top: 0, bottom: 0 }}
      dragElastic={{ top: 0.6, bottom: 0 }}
      onDragEnd={(_, info) => {
        if (info.offset.y < -40) onDismiss()
      }}
      className={`pointer-events-auto relative w-full max-w-md overflow-hidden rounded-2xl p-3 shadow-xl ring-1 ${className}`}
    >
      {children}
    </motion.div>
  )
}
